import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBookOpen,
    faVideo,
    faStar,
    faClock,
    faChalkboardTeacher,
    faUser,
    faPlay,
    faSearch,
    faArrowRight,
    faCertificate,
    faBook
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from "framer-motion";
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import ApiServices from '../Services/ApiServices';
import Header from '../Shared/Header';
import Loader from '../Shared/Loader';
import Pagination from '../Shared/Pagination';
import { encryptId } from '../../utils/encryption';
import htmlHelper from '../../Helpers/htmlHelper';

const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
};



const MyCourses = ({ Auth = null }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        totalPages: 1,
        currentPage: 1,
        totalRecords: 0
    });

    const urlAuthToken = searchParams.get("Auth") || searchParams.get("token");
    const shouldUseUrlAuth = Auth !== null || searchParams.has("Auth") || searchParams.has("token");
    const requestAuthToken = shouldUseUrlAuth ? (urlAuthToken || (typeof Auth === "string" ? Auth : null)) : null;

    const fetchCourses = async (word = '', page = 1) => {
        setLoading(true);
        try {
            const response = await ApiServices.get(
                `MyCourse/getAll?PageSize=9&PageNumber=${page}&IsActive=true&Word=${encodeURIComponent(word)}`
                ,
                {},
                200000,
                requestAuthToken ? { authToken: requestAuthToken } : {}
            );
            if (response.check && response.data) {
                setCourses(response.data.items);
                setPagination({
                    totalPages: response.data.total_pages,
                    currentPage: response.data.current_page,
                    totalRecords: response.data.total_records
                });
            } else {
                setCourses([]);
                setPagination({ totalPages: 1, currentPage: 1, totalRecords: 0 });
            }
        } catch (error) {

            setCourses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses(searchTerm, pagination.currentPage);
    }, [pagination.currentPage, requestAuthToken]);

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchCourses(searchTerm, 1);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="mx-page">
            <Header />
            <br />
            <section className="mx-root" id="my-courses">
                <div className="mx-container">
                    {/* ── header ── */}
                    <motion.div
                        className="mx-header"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div className="mx-eyebrow" variants={fadeUp} custom={0}>
                            <span className="mx-eyebrow-dot" />
                            دوراتي التعليمية
                        </motion.div>
                        <motion.h2 className="mx-title" variants={fadeUp} custom={1}>
                            لوحة <em>متابعة التعلم</em>
                        </motion.h2>
                        <motion.p className="mx-subtitle" variants={fadeUp} custom={2}>
                            استكمل رحلتك التعليمية، شاهد المحاضرات، وقم بتحميل الملحقات الخاصة بكل دورة بسهولة
                        </motion.p>
                    </motion.div>

                    {/* ── Toolbar ── */}
                    <div className="mx-toolbar">
                        <form onSubmit={handleSearchSubmit} className="mx-search-form">
                            <div className="mx-search-box">
                                <FontAwesomeIcon icon={faSearch} className="mx-search-icon" />
                                <input
                                    type="text"
                                    placeholder="ابحث عن دورة أو محاضر..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <button type="submit" className="mx-search-btn">بحث</button>
                            </div>
                        </form>
                    </div>

                    {/* ── Content ── */}
                    <div className="mx-content">
                        {loading ? (
                            <div className="mx-loading-state">
                                <Loader text="جاري تحميل الدورات..." />
                            </div>
                        ) : courses.length === 0 ? (
                            <motion.div
                                className="mx-empty-state"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <div className="mx-empty-icon">📚</div>
                                <h3>لم نجد أي دورات!</h3>
                                <p>جرب البحث بكلمات مختلفة أو استكشف الدورات المتاحة في المنصة</p>
                            </motion.div>
                        ) : (
                            <>
                                <div className="mx-card-grid">
                                    <AnimatePresence mode="popLayout">
                                        {courses.map((course, idx) => (
                                            <CourseCard
                                                key={course.id}
                                                course={course}
                                                index={idx}
                                                onContinue={() => {
                                                    const encryptedId = encryptId(course.id);
                                                    navigate(`/mycourses/${encryptedId}`);
                                                }}
                                            />
                                        ))}
                                    </AnimatePresence>
                                </div>

                                {pagination.totalPages > 1 && (
                                    <div className="mx-pagination-wrapper">
                                        <Pagination
                                            currentPage={pagination.currentPage}
                                            totalPages={pagination.totalPages}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>

            <style>{`
                .mx-root {
                    font-family: "Zain", sans-serif;
                    direction: rtl;
                    position: relative;
                    padding: 80px 0 100px;
                    background: #fff;
                    overflow: hidden;
                    isolation: isolate;
                }

                .mx-container {
                    position: relative; z-index: 2;
                    max-width: 1260px; margin: 0 auto; padding: 0 40px;
                }

                /* Header */
                .mx-header { text-align: center; margin-bottom: 50px; }
                .mx-eyebrow {
                    display: inline-flex; align-items: center; gap: 10px;
                    color: #6366f1; font-weight: 800; font-size: 0.82rem;
                    letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
                }
                .mx-eyebrow-dot {
                    width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
                    animation: mx-pulse 2.2s ease-in-out infinite;
                }
                @keyframes mx-pulse {
                    0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
                    50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
                }
                .mx-title {
                    font-size: clamp(2.2rem, 4vw, 3.6rem);
                    font-weight: 900; color: #0f172a;
                    margin: 0 0 18px; line-height: 1.2;
                }
                .mx-title em {
                    font-style: normal;
                    background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
                    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                .mx-subtitle {
                    font-size: 1.08rem; color: #64748b; line-height: 1.85; margin: 0 auto;
                    max-width: 700px;
                }

                /* Toolbar */
                .mx-toolbar {
                    display: flex; justify-content: center; margin-bottom: 60px;
                }
                .mx-search-form { width: 100%; max-width: 650px; }
                .mx-search-box {
                    position: relative;
                    display: flex; align-items: center; gap: 12px;
                    background: #fff;
                    border: 1px solid rgba(15,23,42,0.08);
                    border-radius: 100px;
                    padding: 6px 6px 6px 20px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.03);
                    transition: all 0.3s;
                }
                .mx-search-box:focus-within {
                    border-color: #6366f1;
                    box-shadow: 0 15px 40px rgba(99,102,241,0.12);
                }
                .mx-search-icon { color: #94a3b8; font-size: 1.1rem; flex-shrink: 0; }
                .mx-search-box input {
                    flex: 1; min-width: 0; width: 100%; border: none; outline: none; background: transparent;
                    padding: 10px 0; font-family: inherit; font-size: 1.05rem;
                    font-weight: 700; color: #1e293b;
                }
                .mx-search-btn {
                    background: #0f172a; color: #fff; border: none;
                    padding: 10px 28px; border-radius: 100px; font-weight: 800;
                    font-family: inherit; cursor: pointer; transition: all 0.2s;
                    white-space: nowrap; flex-shrink: 0;
                }
                .mx-search-btn:hover { background: #1e293b; transform: scale(1.02); }

                /* Grid */
                .mx-card-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 30px;
                }

                /* Premium Card */
                .mx-card {
                    background: #fff;
                    border: 1px solid rgba(15,23,42,0.08);
                    border-radius: 32px;
                    overflow: hidden;
                    display: flex; flex-direction: column;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    position: relative;
                }
                .mx-card:hover {
                    transform: translateY(-12px);
                    box-shadow: 0 30px 60px rgba(15,23,42,0.12);
                    border-color: #6366f122;
                }
                .mx-card-img-wrap {
                    position: relative; height: 220px; overflow: hidden;
                    cursor: pointer;
                }
                .mx-card-img-wrap img {
                    width: 100%; height: 100%; object-fit: cover;
                    transition: transform 0.6s ease;
                }
                .mx-card:hover .mx-card-img-wrap img { transform: scale(1.1); }
                
                .mx-card-overlay {
                    position: absolute; inset: 0; background: rgba(15,23,42,0.4);
                    opacity: 0; transition: 0.3s;
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer;
                }
                .mx-card:hover .mx-card-overlay { opacity: 1; }
                .mx-play-icon {
                    width: 60px; height: 60px; background: #fff; color: #6366f1;
                    border-radius: 50%; display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; transform: scale(0.8); transition: all 0.3s ease;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                }
                .mx-card:hover .mx-play-icon { transform: scale(1); }
                .mx-play-icon:hover { transform: scale(1.15) !important; background: #6366f1; color: #fff; }

                .mx-card-badge {
                    position: absolute; top: 20px; left: 20px;
                    background: #6366f1; color: #fff;
                    padding: 4px 14px; border-radius: 10px;
                    font-size: 0.85rem; font-weight: 800; z-index: 2;
                }
                .mx-card-certified {
                    position: absolute; top: 20px; right: 20px;
                    background: #fff; color: #10b981;
                    width: 34px; height: 34px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 2;
                }

                .mx-card-body { padding: 30px; flex: 1; display: flex; flex-direction: column; }
                .mx-card-instructor { display: flex; align-items: center; gap: 10px; margin-bottom: 15px; }
                .mx-inst-avatar {
                    width: 32px; height: 32px; background: #f1f5f9; color: #6366f1;
                    border-radius: 10px; display: flex; align-items: center; justify-content: center;
                    font-size: 0.8rem; font-weight: 800;
                }
                .mx-inst-name { font-size: 0.85rem; color: #64748b; font-weight: 700; }

                .mx-card-title {
                    font-size: 1.35rem; font-weight: 900; color: #0f172a;
                    margin: 0 0 12px; line-height: 1.4;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                    overflow: hidden; min-height: 3.8rem;
                }
                .mx-card-desc {
                    font-size: 0.95rem; color: #64748b; line-height: 1.6;
                    margin-bottom: 25px;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                    overflow: hidden; min-height: 3rem;
                }

                .mx-card-stats {
                    display: flex; align-items: center; gap: 20px;
                    padding-top: 20px; border-top: 1px solid #f1f5f9; margin-top: auto;
                }
                .mx-card-stat { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: #475569; font-weight: 700; }
                .mx-card-stat i { color: #94a3b8; }
                .mx-card-rate { margin-right: auto; color: #6366f1; font-weight: 900; display: flex; align-items: center; gap: 5px; }

                .mx-card-btn {
                    margin-top: 25px; background: #f8fafc; color: #0f172a;
                    padding: 14px; border-radius: 16px; border: none;
                    width: 100%; font-family: inherit; font-weight: 800;
                    display: flex; align-items: center; justify-content: center; gap: 10px;
                    cursor: pointer; transition: all 0.3s;
                }
                .mx-card:hover .mx-card-btn { background: #0f172a; color: #fff; }
                .mx-card-btn i { transition: transform 0.3s; }
                .mx-card-btn:hover i { transform: translateX(-5px); }

                /* Pagination */
                .mx-pagination-wrapper { margin-top: 60px; display: flex; justify-content: center; }

                /* States */
                .mx-loading-state { padding: 100px 0; }
                .mx-empty-state {
                    text-align: center; padding: 100px 40px;
                    background: #f8fafc; border-radius: 40px;
                    max-width: 600px; margin: 40px auto;
                }
                .mx-empty-icon { font-size: 4rem; margin-bottom: 20px; }
                .mx-empty-state h3 { font-size: 1.8rem; font-weight: 900; color: #0f172a; margin-bottom: 10px; }
                .mx-empty-state p { color: #64748b; margin-bottom: 30px; }

                @media(max-width:1100px){
                    .mx-card-grid { grid-template-columns: repeat(2, 1fr); }
                }
                @media(max-width:768px){
                    .mx-root { padding: 40px 0 60px; }
                    .mx-container { padding: 0 16px; }
                    .mx-header { margin-bottom: 30px; }
                    .mx-eyebrow { margin-bottom: 12px; font-size: 0.78rem; }
                    .mx-title { font-size: clamp(1.8rem, 5vw, 2.5rem); margin-bottom: 12px; }
                    .mx-subtitle { font-size: 0.95rem; line-height: 1.7; }
                    .mx-toolbar { margin-bottom: 35px; }
                    .mx-search-box { padding: 5px 5px 5px 14px; gap: 8px; }
                    .mx-search-icon { font-size: 1rem; }
                    .mx-search-box input { font-size: 0.95rem; padding: 8px 0; }
                    .mx-search-btn { padding: 8px 18px; font-size: 0.9rem; }
                    .mx-card-grid { grid-template-columns: 1fr; gap: 20px; }
                    .mx-card { border-radius: 24px; }
                    .mx-card-img-wrap { height: 190px; }
                    .mx-card-overlay { opacity: 0.85; background: rgba(15,23,42,0.25); }
                    .mx-play-icon { transform: scale(0.9); }
                    .mx-card-body { padding: 20px; }
                    .mx-card-title { font-size: 1.2rem; min-height: auto; margin-bottom: 8px; }
                    .mx-card-desc { font-size: 0.88rem; min-height: auto; margin-bottom: 18px; }
                    .mx-card-stats { padding-top: 14px; gap: 12px; font-size: 0.8rem; }
                    .mx-card-btn { margin-top: 18px; padding: 12px; font-size: 0.95rem; }
                    .mx-empty-state { padding: 50px 20px; border-radius: 24px; }
                    .mx-empty-icon { font-size: 3rem; }
                    .mx-empty-state h3 { font-size: 1.4rem; }
                }
                @media(max-width:480px){
                    .mx-search-box { padding: 4px 4px 4px 10px; gap: 6px; }
                    .mx-search-box input { font-size: 0.9rem; }
                    .mx-search-btn { padding: 8px 14px; font-size: 0.85rem; }
                    .mx-card-stats { flex-wrap: wrap; gap: 10px 16px; }
                }
            `}</style>
        </div>
    );
};

const CourseCard = ({ course, index, onContinue }) => (
    <motion.div
        className="mx-card"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={index % 3}
        layout
    >
        <div
            className="mx-card-img-wrap"
            onClick={onContinue}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onContinue(); }}
            title="مشاهدة محتوى الدورة"
        >
            <img src={getFullFileUrl(course.course_cover)} alt={course.name} />
            <div className="mx-card-overlay">
                <div className="mx-play-icon" title="مشاهدة محتوى الدورة">
                    <FontAwesomeIcon icon={faPlay} />
                </div>
            </div>
            {course.category && <span className="mx-card-badge">{course.category}</span>}
            {course.is_certified && (
                <div className="mx-card-certified" title="دورة معتمدة">
                    <FontAwesomeIcon icon={faCertificate} />
                </div>
            )}
        </div>

        <div className="mx-card-body">
            <div className="mx-card-instructor">
                <div className="mx-inst-avatar">
                    <FontAwesomeIcon icon={faUser} />
                </div>
                <span className="mx-inst-name">{course.instructor}</span>
            </div>

            <h3 className="mx-card-title">{course.name}</h3>
            <p className="mx-card-desc">
                {htmlHelper.stripHtml(course.description) || "استمتع بتجربة تعليمية مميزة مع أفضل المحاضرين والخبراء في هذا المجال."}
            </p>

            <div className="mx-card-stats">
                <div className="mx-card-stat">
                    <FontAwesomeIcon icon={faClock} />
                    <span>{course.hours || 0} ساعة</span>
                </div>
                <div className="mx-card-stat">
                    <FontAwesomeIcon icon={faBook} />
                    <span>{course.material || 0} درس</span>
                </div>
                <div className="mx-card-rate">
                    <FontAwesomeIcon icon={faStar} />
                    <span>{course.rate || 0}</span>
                </div>
            </div>

            <button className="mx-card-btn" onClick={onContinue}>
                <span>متابعة التعلم</span>
                <FontAwesomeIcon icon={faArrowRight} />
            </button>
        </div>
    </motion.div>
);

export default MyCourses;
